import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus } from "lucide-react";

/**
 * Type-ahead combobox for inventory items.
 * - Filters as the user types, showing item name + stock + last rate.
 * - Free-form entry (item not in catalog) is allowed and surfaces a "New item" hint;
 *   the backend auto-creates it on PO save.
 *
 * Props:
 *   items       — array of inventory items {id, name, unit, category, stock_qty, last_rate}
 *   value       — { item_id, item_name }
 *   onPick(it)  — called with the full item object when a catalog item is chosen
 *   onFreeText(text) — called when the user types a name not in the catalog
 *   inputTestId — data-testid for the input
 */
export default function ItemCombobox({ items, value, onPick, onFreeText, inputTestId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value?.item_name || "");
  const wrapRef = useRef(null);

  useEffect(() => { setQuery(value?.item_name || ""); }, [value?.item_name, value?.item_id]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, 40);
    return items.filter((it) => (it.name || "").toLowerCase().includes(q)).slice(0, 40);
  }, [items, query]);

  const isCatalog = !!value?.item_id;
  const isNewItem = !isCatalog && !!(value?.item_name || "").trim();
  const exactMatch = items.find((it) => (it.name || "").toLowerCase() === (query || "").trim().toLowerCase());

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          data-testid={inputTestId}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // As the user types, treat it as free-form until they pick from the list.
            const match = items.find((it) => (it.name || "").toLowerCase() === e.target.value.trim().toLowerCase());
            if (match) onPick(match); else onFreeText(e.target.value);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Type to search or add new…"
          className="block w-full border border-[hsl(var(--rule))] p-1.5 pl-6 text-xs bg-[hsl(var(--bg))]"
        />
        <Search size={11} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-soft))]" />
      </div>
      {/* Contextual hint under the input */}
      {isNewItem && !exactMatch && (
        <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-[hsl(var(--brass-2))]">
          <Plus size={10} /> New item — will be added to catalog
        </div>
      )}
      {isCatalog && exactMatch && (
        <div className="mt-0.5 text-[10px] text-[hsl(var(--ink-soft))]">
          Stock: <span className={Number(exactMatch.stock_qty) <= Number(exactMatch.min_qty || 0) ? "text-[hsl(6_62%_45%)] font-semibold" : "text-[hsl(var(--forest))]"}>
            {Number(exactMatch.stock_qty || 0).toLocaleString("en-IN")} {exactMatch.unit}
          </span>
          {exactMatch.last_rate ? <> · Last rate ₹{Number(exactMatch.last_rate).toLocaleString("en-IN")}</> : null}
        </div>
      )}

      {open && filtered.length > 0 && (
        <ul className="absolute z-30 mt-1 w-72 max-w-[90vw] max-h-64 overflow-auto border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] shadow-lg text-xs">
          {filtered.map((it) => {
            const low = Number(it.stock_qty) <= Number(it.min_qty || 0);
            return (
              <li key={it.id}>
                <button
                  type="button"
                  data-testid={`item-opt-${it.id}`}
                  onMouseDown={(e) => { e.preventDefault(); onPick(it); setQuery(it.name); setOpen(false); }}
                  className="w-full text-left px-2 py-1.5 hover:bg-[hsl(var(--bg-2))] flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{it.name}</div>
                    <div className="text-[10px] text-[hsl(var(--ink-soft))] truncate">{it.category} · {it.unit}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={low ? "text-[10px] text-[hsl(6_62%_45%)]" : "text-[10px] text-[hsl(var(--forest))]"}>
                      {Number(it.stock_qty || 0).toLocaleString("en-IN")} {it.unit}
                    </div>
                    {it.last_rate ? <div className="text-[10px] text-[hsl(var(--ink-soft))]">₹{Number(it.last_rate).toLocaleString("en-IN")}</div> : null}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
